import type { ICookies, IOptions } from 'cookies'
import type { CookieAttributes, CookiesStatic } from 'js-cookie'
import { v4 as uuid } from 'uuid'

import {
  AD_SERVER_BROWSER_ID_CONVERSION_COOKIE,
  AD_SERVER_BROWSER_ID_TARGETING_COOKIE,
  AD_SERVER_MEDIA_CODE_SEARCH_PARAM,
} from 'shared/consts/adServer'
import {
  AD_SERVER,
  AD_SERVER_BANNER,
  AD_SERVER_CROSSSELL_PRODUCT,
  AD_SERVER_SEARCH_PRODUCT,
  DEFAULT_2_AD_SERVER_VARIANT,
  type ExperimentName,
  KEVEL_AD_SERVER_VARIANT,
  NON_DEFAULT_AD_SERVER_VARIANTS,
} from 'shared/experiments/consts'
import {
  activateExperiment,
  getExperiments,
} from 'shared/experiments/utils/experiments'
import logger from 'shared/services/logger'
import { isCookieConsent } from 'shared/utils/cookieConsent'
import { mapValues } from 'shared/utils/objectUtils'
import type { AdServerTrackingUrlsWithPzn } from 'types/adServerTypes'
import { type Experiment } from 'types/shopConfig'

export type ServerCookie = ICookies
export type ServerCookieOptions = Omit<IOptions, 'expires' | 'sameSite'>

export type ClientCookie = CookiesStatic
export type ClientCookieOptions = Omit<CookieAttributes, 'sameSite'>

export const defaultCookieOptions: ServerCookieOptions | ClientCookieOptions = {
  path: '/',
  httpOnly: false,
}

const getTTL = (cookieName: string): number =>
  cookieName === AD_SERVER_BROWSER_ID_CONVERSION_COOKIE ? 90 : 7

const getClientCookieOptions = (cookieName: string): ClientCookieOptions => {
  return {
    ...defaultCookieOptions,
    expires: getTTL(cookieName),
  }
}

const getServerCookieOptions = (cookieName: string): ServerCookieOptions => {
  const multiplier: number = 24 * 60 * 60 * 1000
  return {
    ...defaultCookieOptions,
    maxAge: getTTL(cookieName) * multiplier,
  }
}

export const getCookieOptions = (
  cookieName: string,
  onServer: boolean
): ClientCookieOptions | ServerCookieOptions => {
  return onServer
    ? getServerCookieOptions(cookieName)
    : getClientCookieOptions(cookieName)
}

const isExperimentEnabled = (
  experiments: Experiment[],
  experimentName: ExperimentName
): boolean => {
  const adServerFeatureFlagVariant = getExperiments(
    [experimentName],
    experiments
  )?.[0]?.variant

  return NON_DEFAULT_AD_SERVER_VARIANTS.includes(
    adServerFeatureFlagVariant as string
  )
}

// checks if ad-server experiment variant is non-default, supported variant
export const isAdServerEnabled = (experiments: Experiment[] = []) =>
  isExperimentEnabled(experiments, AD_SERVER)

export const isAdServerSearchExperimentEnabled = (
  experiments: Experiment[] = []
) =>
  isAdServerEnabled(experiments) &&
  isExperimentEnabled(experiments, AD_SERVER_SEARCH_PRODUCT)

export const isAdServerCrossSellExperimentEnabled = (
  experiments: Experiment[] = []
) =>
  isAdServerEnabled(experiments) &&
  isExperimentEnabled(experiments, AD_SERVER_CROSSSELL_PRODUCT)

export const isAdServerBannerExperimentEnabled = (
  experiments: Experiment[] = []
) =>
  isAdServerEnabled(experiments) &&
  isExperimentEnabled(experiments, AD_SERVER_BANNER)

export const shouldTriggerAdServerExperimentActivation = (
  experiments: Experiment[] = []
) => {
  const variant = getExperiments([AD_SERVER], experiments)?.[0]?.variant
  return (
    variant === KEVEL_AD_SERVER_VARIANT ||
    variant === DEFAULT_2_AD_SERVER_VARIANT
  )
}

export const activateAdServerExperiment = async () => {
  try {
    await activateExperiment(AD_SERVER)
  } catch (error) {
    logger.error('Failed to activate a/b test for ad-server', {
      // @ts-ignore as `logger` is not typescript module
      error,
    })
  }
}

export const getTargetingKeywordsFromSearchParams = (
  queryParams: string | URLSearchParams
): Array<string> => {
  const searchParams =
    queryParams instanceof URLSearchParams
      ? queryParams
      : new URLSearchParams(queryParams)

  const queryKey = Array.from(searchParams.keys()).find(
    key => key.toLowerCase() === AD_SERVER_MEDIA_CODE_SEARCH_PARAM
  )

  if (!queryKey) {
    return []
  }

  return searchParams.get(queryKey)?.split(',').filter(Boolean) ?? []
}

export const getSetAdUserIdentity = (cookies: ServerCookie | ClientCookie) => {
  const onServer: boolean = 'request' in cookies

  let browserIdConversion = cookies.get(AD_SERVER_BROWSER_ID_CONVERSION_COOKIE)
  if (!browserIdConversion) {
    cookies.set(
      AD_SERVER_BROWSER_ID_CONVERSION_COOKIE,
      uuid(),
      getCookieOptions(AD_SERVER_BROWSER_ID_CONVERSION_COOKIE, onServer)
    )
    browserIdConversion = cookies.get(AD_SERVER_BROWSER_ID_CONVERSION_COOKIE)
  }

  let browserIdTargeting
  if (isCookieConsent(cookies)) {
    browserIdTargeting = cookies.get(AD_SERVER_BROWSER_ID_TARGETING_COOKIE)
    if (!browserIdTargeting) {
      cookies.set(
        AD_SERVER_BROWSER_ID_TARGETING_COOKIE,
        uuid(),
        getCookieOptions(AD_SERVER_BROWSER_ID_TARGETING_COOKIE, onServer)
      )
      // make sure that we set properly cookie for targeting browser id
      browserIdTargeting = cookies.get(AD_SERVER_BROWSER_ID_TARGETING_COOKIE)
    }
  }

  return {
    browserIdConversion,
    browserIdTargeting,
  }
}

export const extractClickUrlsFromTrackingUrls = (
  trackingUrls: AdServerTrackingUrlsWithPzn
) => {
  try {
    return mapValues<AdServerTrackingUrlsWithPzn, { clickUrl: string }>(
      urls => ({ 'clickUrl': urls.clickUrl }),
      trackingUrls
    )
  } catch (e) {
    logger.error(`Failed to extract clickUrls from trackingUrls: ${e}`)
    return {}
  }
}

export const extractJParamFromTrackingUrl = (
  url: string
): string[] | string | null => {
  try {
    const parsedUrl = new URL(url)
    const searchParams = new URLSearchParams(parsedUrl.searchParams)

    return searchParams.get('j')
  } catch (e) {
    logger.error(`Failed to extract j param the tracking url: ${e}`)
    return null
  }
}

export const extractHostAndPathFromTrackingUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    const { origin, pathname } = parsedUrl
    return origin + pathname
  } catch (e) {
    logger.error(`Failed to extract host and path from the tracking url: ${e}`)
    return null
  }
}
